import { Button, Group } from '@mantine/core';

import {
  Batch,
  BatchStatusEnum,
  BatchTypeEnum,
  DeclarationCustomsFlowType,
  DeclarationStatus,
  EnvironmentCountries,
  getTotal,
  TemplateType,
  TransactionTypes,
} from '@e-origin/shared';

import { useSettings } from '@hooks/useSettings';
import { useBatchModalManager } from './batchModalManager';

const canInvalidateLowValueBatch = (batch: Batch, country: string): boolean => {
  const totalNotInvalidated = batch.statistics.total - (batch.statistics.invalidationPending || 0);

  if (country === EnvironmentCountries.BE && batch.templateType === TemplateType.LOW_VALUE_H7) {
    return batch.statistics.released > 0 && totalNotInvalidated > 0;
  }

  const released = getTotal(batch, DeclarationStatus.RELEASED, DeclarationCustomsFlowType.POST);

  if (country === EnvironmentCountries.NL) {
    const acceptedInPost = getTotal(batch, DeclarationStatus.ACCEPTED, DeclarationCustomsFlowType.POST);
    const acceptedInPre = getTotal(batch, DeclarationStatus.ACCEPTED, DeclarationCustomsFlowType.PRE);

    const totalAccepted = acceptedInPost + acceptedInPre;

    return (released > 0 || totalAccepted > 0) && totalNotInvalidated > 0;
  }

  return released > 0 && totalNotInvalidated > 0;
};

const canInvalidateHighValueBatch = (batch: Batch): boolean => {
  return batch.statistics?.accepted > 0 || batch.statistics?.preAccepted > 0 || batch.statistics?.released > 0;
};

const canBulkUpdate = (batch: Batch, isHighValue: boolean): boolean => {
  if (!isHighValue) {
    return true;
  }

  return batch.statistics?.analysis > 0 || batch.statistics?.rejected > 0 || batch.statistics?.notSent > 0;
};

const canLoadNewFile = (batch: Batch, country: string, isHighValue: boolean): boolean => {
  if (batch.status.value === BatchStatusEnum.FAILED) {
    return true;
  }

  if (isHighValue && (batch.statistics?.rejected > 0 || batch.statistics.notSent > 0 || batch.statistics.analysis > 0)) {
    return true;
  }

  if (country === EnvironmentCountries.NL && isHighValue && batch.transactionType === TransactionTypes.B2B_MULTIPLE) {
    return batch.statistics?.accepted > 0 || batch.statistics?.preAccepted > 0 || batch.statistics?.released > 0 || batch.statistics?.rejected > 0;
  }

  if (isHighValue && (batch.statistics?.accepted > 0 || batch.statistics?.preAccepted > 0 || batch.statistics?.released > 0)) {
    return false;
  }

  return batch.statistics?.rejected > 0 || batch.statistics?.notSent > 0;
};

interface UpdateBatchProps {
  isHighValue: boolean;
  batch: Batch;
  batchType: BatchTypeEnum;
  afterCreate: () => void;
  onHide: () => void;
}

export const UpdateBatch: React.FC<UpdateBatchProps> = (props) => {
  const batchModalManager = useBatchModalManager();
  const { isHighValue, onHide, batch, batchType, afterCreate } = props;

  const { data: settings } = useSettings();

  const canInvalidateBatch = isHighValue ? canInvalidateHighValueBatch(batch) : canInvalidateLowValueBatch(batch, settings?.country);

  return (
    <Group justify="center" wrap="nowrap" pt="md">
      <Button
        data-pw="load-new-file-btn"
        type="button"
        disabled={!canLoadNewFile(batch, settings?.country, isHighValue)}
        onClick={() => {
          onHide();
          batchModalManager.openNewBatch({
            isUpdate: true,
            batch,
            batchType,
            afterCreate,
          });
        }}
        variant="filled"
        size="lg"
      >
        Load a new file
      </Button>
      <Button
        data-pw="bulk-update-btn"
        disabled={!canBulkUpdate(batch, isHighValue)}
        type="button"
        onClick={() => {
          onHide();
          batchModalManager.openBulkUpdateBatch({
            batch,
            afterCreate,
          });
        }}
        variant="filled"
        size="lg"
      >
        Bulk Update
      </Button>
      <Button
        disabled={!canInvalidateBatch}
        type="button"
        onClick={() => {
          onHide();
          batchModalManager.openInvalidateBatch({
            batch,
            afterCreate,
          });
        }}
        variant="filled"
        size="lg"
      >
        Invalidate batch
      </Button>
    </Group>
  );
};
